import {AudioStreamState, SttClientState, VideoPinParticipant, WebRTCServerSource} from 'enums';
import {
    SessionActivityName, AsyncRequestTypeName, SessionScreenshot, AgentSession, AgentSessionSettings, Group,
    ScreenSharingActivityStatus, SessionActivityTextChatDataState, SessionGoal, ParticipantAsyncRequest, AnyParticipant,
    SessionActivity,
} from 'interfaces';
import {
    WebRTCConnectDetails, TextChatThreads, TextChatMessage, VirtualBackgroundConfig,
    FilesAreaContent, TranslationSettings, ComplianceDecisionMeta
} from 'types';

export type ResolvedAsyncRequest = {
    participantId: string;
    request: ParticipantAsyncRequest;
}

export enum DevicePermission {
    Audio = 'audio',
    Video = 'video'
}

export type UserSessionResponsePayload = {
    session: AgentSession;
    settings?: AgentSessionSettings;
}

export enum SidebarView {
    Participants = 'participants',
    VirtualBackground = 'virtual-background',
    Transcriptions = 'transcriptions',
    TextChat = 'text-chat',
    TextChatHistory = 'text-chat-history',
    AvatarHistory = 'avatar-history',
    Notes = 'notes',
    Screenshots = 'screenshots',
    SIP = 'sip',
    PreSessionFlow = 'pre-session-flow',
    AgentChatbot = 'agent-chatbot',
}

export enum InSessionAction {
    ToggleActivity = 'toggle-activity',
    ToggleOnHold = 'toggle-on-hold',
    Reassign = 'reassign',
    Deescalate = 'deescalate',
    ToggleAsyncRequest = 'toggle-async-request',
    ToggleScreenshot = 'toggle-screenshot',
    ToggleParticipantDevicePermission = 'toggle-participant-device-permission'
}

export type InSessionActionData = {
    devicePermission?: DevicePermission;
    participantId?: string;
    activityName?: SessionActivityName;
    activityId?: string;
    asyncRequestType?: AsyncRequestTypeName;
    screenshotName?: string;
}

export type InProgressInSessionAction = {
    id: string;
    action: InSessionAction;
    data?: InSessionActionData;
    error?: Error;
}

export type HandleInSessionAction<T = any> = (
    promise: Promise<T>, action?: InSessionAction, data?: InSessionActionData
) => Promise<T>;
export type handleInSessionActionRequest = HandleInSessionAction<UserSessionResponsePayload>;

export type FromInSessionContextProps = {
    sidebarView: SidebarView;
    inProgressActions: InProgressInSessionAction[];
    localSessionEnded: boolean;
    setLocalSessionEnded: (value: boolean) => void;
    handleInSessionAction: HandleInSessionAction;
    handleInSessionActionRequest: handleInSessionActionRequest;
    onSidebarViewChange: (view: SidebarView) => void;
    openDefaultPage: () => void;
}

export type SessionActionsContextProps = {
    audioEnabled: boolean;
    videoEnabled: boolean;
    fullScreenVideo: boolean;
    screenshotsPreview: boolean;
    deleteScreenshotInProgress: boolean;
    participantsNetworkQuality: {[participantId: string]: number};
    videoDevices: MediaDeviceInfo[];
    audioDevices: MediaDeviceInfo[];
    videoPinParticipant: VideoPinParticipant;
    pinnedParticipantId?: string;
    goalToConfirm: SessionGoal;
    hasUnreadMessages: boolean;
    feedbackSent: boolean;
    groups: Group[];
    textChatThreads: TextChatThreads;
    subtitlesThreads: TextChatThreads;
    agentChatbotThreads?: TextChatThreads;
    aiSuggestionsLoading?: boolean;
    aiSuggestions?: {text: string}[];
    aiSuggestionsError?: boolean;
    aiSuggestionsAudioLoading?: boolean;
    aiSuggestionsAudio?: {text: string}[];
    aiSuggestionsAudioError?: boolean;
    virtualBackgroundConfig?: VirtualBackgroundConfig;
    tracks: MediaStreamTrack[];
    virtualBackgroundContent?: FilesAreaContent;
    localSessionEnded: boolean;
    deescalated: boolean;
    updateTranscriptionSettings: (activityId: string, data: TranslationSettings) => void;
    requestAiSuggestions?: () => void;
    requestAiSuggestionsAudio?: () => void;
    canMakeScreenshot: (screenshotName: string) => boolean;
    setGoalToConfirm: (goal: SessionGoal) => void;
    confirmGoal: () => void;
    setMediaDevices: (devices: MediaDeviceInfo[]) => void;
    onAudioStreamingToggle: (activityId: string, value: boolean) => void;
    audioStreamState: AudioStreamState | SttClientState;
    setAudioStreamState: (state: AudioStreamState | SttClientState) => void;
    setTracks: (tracks: MediaStreamTrack[]) => void;
    toggleActivity: (name: SessionActivityName) => void;
    reassign: () => void;
    changeAssignment?: () => void;
    onParticipantUpdate?: (participant?: AnyParticipant) => void;
    onParticipantRemove?: (participant?: AnyParticipant) => void;
    toggleOnHold: () => void;
    openInviteModal: (participant?: AnyParticipant) => void;
    toggleGoalsModal: () => void;
    requestEndSession: (data?: {success: boolean}) => void;
    leaveSession: () => void;
    setScreenSharingActivityStatus: (status: ScreenSharingActivityStatus) => void;
    onStopScreenSharing: (error: Error) => void;
    notifyWebRTCStartSuccess: (source: WebRTCServerSource, connectStatus?: WebRTCConnectDetails) => void;
    notifyWebRTCStartFailed: (source: WebRTCServerSource, error: WebRTCConnectDetails) => void;
    requestUpdateParticipantDevicePermission: (participantId: string, type: DevicePermission, disabled: boolean) =>
        Promise<UserSessionResponsePayload>;
    onToggleFullScreenVideo: (value?: boolean) => void;
    onToggleScreenshotsPreview: () => void;
    onToggleAudio: () => void;
    onToggleVideo: () => void;
    onLocalNetworkQualityLevelChange: (level: number) => void;
    onRemoteNetworkQualityLevelChange: (level: number) => void;
    handleVideoPinParticipantUpdate: (participant: VideoPinParticipant, participantId?: string) => void;
    onScreenshotClick: (screenshot: SessionScreenshot, participantId?: string) => void;
    onRequestDeleteScreenShot: (screenshot: SessionScreenshot) => void;
    loadScreenshotPreview: (screenshot: SessionScreenshot) => Promise<any>;
    createParticipantAsyncRequest: (participantId: string, requestType: AsyncRequestTypeName) => void;
    resetParticipantAsyncRequest: (participantId: string, requestType: AsyncRequestTypeName) => void;
    showParticipantAsyncRequestDetails: (participantId: string, requestType: AsyncRequestTypeName) => void;
    fetchAsyncRequestScreenshot: (participantId: string, requestType: AsyncRequestTypeName) => Promise<string>;
    onTextChatNewMessage?: () => void;
    onTextChatFileDownload: (fileName: string, activityId: string) => Promise<any>;
    onTextChatSubmitMessage: (message: Partial<TextChatMessage>, compliance?: ComplianceDecisionMeta) => Promise<any>;
    onTextChatChangeState?: (value: SessionActivityTextChatDataState) => void;
    askAgentChatbot?: (question: string) => Promise<any>;
    transferToBot?: () => void;
    toggleFeedbackModal?: () => void;
    setMediaDevicesDialogOpen?: (value: boolean) => void;
    setDeescalateDialogOpen?: (value: boolean) => void;
    onWebRTCConnectError?: () => void;
    onTogglePictureInPicture?: () => void;
    setVirtualBackgroundConfig?: (value: VirtualBackgroundConfig) => void;
    onActivityParticipantLanguageChange?: (activity: SessionActivity, participantId: string, language: string) => void;
    updateVirtualBackground?: (value: VirtualBackgroundConfig) => void;
}

export type UserSessionContextProps = FromInSessionContextProps & UserSessionResponsePayload & SessionActionsContextProps;
